<template>
  <div>
    <ResponsiveHUD v-if="isDev" />
    <BannerOpenHouse
      v-if="hasOpenHouses"
      :open-houses="openHouses"
    />
    <component
      :is="section.component"
      v-bind="{ ...sectionProps, ...section.props }"
      v-for="(section) in activeSections"
      :key="section.id"
    />
  </div>
</template>

<script>
import { markRaw } from 'vue';
import getOpenHouses from './helpers/getOpenHouses';
import iconUrl from './assets/tgi-icon.jpeg';
import ResponsiveHUD from './components/ResponsiveHUD.vue';
import PropertyBrokerInfo from './sections/PropertyBrokerInfo.vue';
import PropertyDescription from './sections/PropertyDescription.vue';
import PropertyFooter from './sections/PropertyFooter.vue';
import PropertyHeader from './sections/PropertyHeader.vue';
import PropertyHome from './sections/PropertyHome.vue';
import PropertyMap from './sections/PropertyMap.vue';
import PropertyPhotos from './sections/PropertyPhotos.vue';
import PropertyRequestInfo from './sections/PropertyRequestInfo.vue';
import PropertyVideo from './sections/PropertyVideo.vue';
import PropertyDetails from './sections/PropertyDetails.vue';
import getPropertyVirtualTourUrl from './helpers/getPropertyVirtualTourUrl';
import BannerOpenHouse from './components/Banners/BannerOpenHouse.vue';

export default {
  name: 'Theme',
  components: { BannerOpenHouse, ResponsiveHUD },
  props: {
    appleMapKitToken: {
      required: true,
      type: String,
    },
    isDev: {
      required: true,
      type: Boolean,
    },
    isBranded: {
      required: true,
      type: Boolean,
    },
    myTribusEmbed: {
      required: true,
      type: Object,
    },
    property: {
      required: true,
      type: Object,
    },
  },

  data() {
    return {
      isReady: false,
      listAgents: [],
      sections: [
        {
          component: markRaw(PropertyHeader),
          id: 'top',
          title: null,
        },
        {
          component: markRaw(PropertyHome),
          id: 'home',
          title: 'Home',
        },
        {
          component: markRaw(PropertyDescription),
          id: 'description',
          title: 'Description',
        },
        {
          component: markRaw(PropertyBrokerInfo),
          id: 'broker-info',
          title: 'Offered By',
        },
        {
          component: markRaw(PropertyDetails),
          id: 'property-details',
          title: 'Property Details',
        },
        {
          component: markRaw(PropertyVideo),
          id: 'video',
          title: 'Video',
        },
        {
          component: markRaw(PropertyPhotos),
          id: 'photos',
          title: 'Photos',
        },
        {
          component: markRaw(PropertyMap),
          id: 'map',
          title: 'Map',
        },
        {
          component: markRaw(PropertyRequestInfo),
          id: 'request-info',
          title: 'Request Info',
        },
        {
          component: markRaw(PropertyFooter),
          id: 'footer',
          title: null,
        },
      ],
    };
  },

  computed: {
    hasOpenHouses() {
      return this.openHouses.length > 0;
    },
    openHouses() {
      return getOpenHouses(this.property);
    },
    /** @type {array} */
    activeSections() {
      const displayProps = {
        isBranded: this.isBranded,
        listAgents: this.listAgents,
        property: this.property,
      };

      return this.sections
        .filter((section) => (typeof section.component.display === 'function'
          ? section.component.display(displayProps)
          : true))
        .map((section) => ({
          ...section,
          props: {
            id: section.id,
          },
        }));
    },

    /** @type {object} */
    menuItems() {
      const menuItems = {};

      this.activeSections.forEach((section) => {
        if (section.title === null) {
          return;
        }

        menuItems[`#${section.id}`] = section.title;
      });

      return menuItems;
    },

    /** @type {object} */
    sectionProps() {
      return {
        appleMapKitToken: this.appleMapKitToken,
        branded: this.isBranded,
        listAgents: this.listAgents,
        loading: !this.isReady,
        menuItems: this.menuItems,
        myTribusEmbed: this.myTribusEmbed,
        property: this.property,
        shouldShowVirtualTourButton: this.shouldShowVirtualTourButton,
      };
    },

    /** @type {boolean} */
    shouldShowVirtualTourButton() {
      const propertyVirtualTourUrl = getPropertyVirtualTourUrl(this.property);
      const isBrandedAndHasTour = this.isBranded && propertyVirtualTourUrl;

      return !!isBrandedAndHasTour;
    },
  },

  async created() {
    this.setFavicon();
    await this.setListAgents();

    this.isReady = true;
  },

  methods: {
    /** @return {void} */
    setFavicon() {
      const faviconLink = document.querySelector("link[rel*='icon']")
        || document.createElement('link');

      faviconLink.type = 'image/x-icon';
      faviconLink.rel = 'shortcut icon';
      faviconLink.href = iconUrl;

      document.getElementsByTagName('head')[0].appendChild(faviconLink);
    },

    /** @return {Promise<void>} */
    async setListAgents() {
      this.listAgents = (await Promise
        .allSettled([
          this.property.getUserByAgentKey('list_agent'),
          this.property.getUserByAgentKey('co_list_agent'),
        ]))
        .filter((p) => p.status === 'fulfilled' && p.value !== null)
        .map((p) => p.value);
    },
  },
};
</script>

<!--suppress CssUnusedSymbol, CssUnknownTarget -->
<style>
@import 'tailwindcss/base';
@import 'tailwindcss/components';
@import 'tailwindcss/utilities';
@import url('https://fonts.googleapis.com/css2?family=Noto+Sans:wght@300;400;700&display=swap');

body {
  font-family: 'Noto Sans', sans-serif;
}

.width-lg {
  max-width: 1400px;
}

.width-sm {
  max-width: 900px;
}
</style>
